import React, { useCallback, useMemo, useState } from "react";
import { PlainButton, TextInput } from "@jugl-web/ui-components";
import { TextAreaInput } from "@jugl-web/ui-components/cross-platform/forms/TextAreaInput";
import { cx, useTranslations } from "@jugl-web/utils";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { KnowledgeItem } from "@jugl-web/rest-api/ai-workflows/types";
import { Button } from "@jugl-web/ui-kit";
import { ReactComponent as QuestionIcon } from "./assets/question.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { AgentKnowledgeContentHeader } from "../../../AgentKnowledgeContentHeader";
import { useAgentKnowledge } from "../../../../../../../../hooks/useAgentKnowledge";

export const QuestionsKnowledgeForm: React.FC<{
  entityId: string;
  isDisabled: boolean;
  isFirstItem: boolean;
  editedKnowledge?: KnowledgeItem<"ques">;
  onRequestClose: () => void;
}> = ({
  entityId,
  isFirstItem,
  isDisabled,
  editedKnowledge,
  onRequestClose,
}) => {
  const { t } = useTranslations();
  const { aiWorkflowsApi } = useRestApiProvider();

  const [question, setQuestion] = useState(
    editedKnowledge?.data.source[0]?.question || ""
  );
  const [answer, setAnswer] = useState(
    editedKnowledge?.data.source[0]?.answer || ""
  );
  const [isLoading, setIsLoading] = useState(false);

  const [uploadQuestionKnowledge] =
    aiWorkflowsApi.useUploadQuestionKnowledgeMutation();
  const [updateQuestionKnowledge] =
    aiWorkflowsApi.useUpdateQuestionKnowledgeMutation();
  const { handleCXApiError } = useAgentKnowledge(entityId);

  const handleCreateQuestionKnowledge = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = {
        agentId: entityId,
        entityId,
        content: [{ question: question.trim(), answer: answer.trim() }],
      };
      if (editedKnowledge) {
        await updateQuestionKnowledge({
          ...data,
          id: editedKnowledge.data.id,
        }).unwrap();
      } else {
        await uploadQuestionKnowledge(data).unwrap();
      }

      onRequestClose();
    } catch (error) {
      handleCXApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    answer,
    editedKnowledge,
    entityId,
    handleCXApiError,
    onRequestClose,
    question,
    updateQuestionKnowledge,
    uploadQuestionKnowledge,
  ]);

  const headerTitle = useMemo(() => {
    if (editedKnowledge) {
      return t({
        id: "cx-ai-agents-page.edit-questions-and-answers",
        defaultMessage: "Edit Q&A",
      });
    }

    if (isFirstItem) {
      return t({
        id: "cx-ai-agents-page.questions-and-answers",
        defaultMessage: "Q&A",
      });
    }

    return t({
      id: "cx-ai-agents-page.new-questions-and-answers",
      defaultMessage: "New Q&A",
    });
  }, [editedKnowledge, isFirstItem, t]);

  return (
    <div className="flex h-full w-full flex-col bg-white">
      <AgentKnowledgeContentHeader
        title={headerTitle}
        description={
          isFirstItem
            ? t({
                id: "cx-ai-agents-page.add-specific-answers-to-questions",
                defaultMessage:
                  "Add specific answers to questions, so Agent will always answer correct",
              })
            : undefined
        }
        icon={<QuestionIcon />}
        startContent={
          !isFirstItem ? (
            <PlainButton
              className="flex h-6 w-10 shrink-0 items-center justify-center"
              onClick={onRequestClose}
              disabled={isLoading}
            >
              <ArrowIcon />
            </PlainButton>
          ) : undefined
        }
      />
      <div className="jugl__custom-scrollbar my-8 flex justify-center overflow-y-auto px-10">
        <div className="flex w-full max-w-[800px] flex-col gap-10">
          <div className="flex flex-col gap-6">
            <TextInput
              labelPosition="border-overlap"
              label={t({
                id: "cx-ai-agents-page.question",
                defaultMessage: "Question",
              })}
              placeholder={t({
                id: "common.enter",
                defaultMessage: "Enter",
              })}
              value={question}
              onChange={(e) => setQuestion(e.target.value.trimStart())}
              autoFocus
              isDisabled={isLoading || isDisabled}
            />
            <TextAreaInput
              labelPosition="border-overlap"
              label={t({
                id: "cx-ai-agents-page.text",
                defaultMessage: "Text",
              })}
              placeholder={t({
                id: "cx-ai-agents-page.enter-answer",
                defaultMessage: "Enter answer",
              })}
              value={answer}
              onChange={(e) => setAnswer(e.target.value.trimStart())}
              classNames={{
                inputClassName: cx(
                  "min-h-[120px]",
                  (isLoading || isDisabled) && "cursor-not-allowed"
                ),
              }}
              isDisabled={isLoading || isDisabled}
            />
            <div className="ml-auto flex items-center gap-4">
              {editedKnowledge && !isDisabled && (
                <Button
                  variant="tertiary"
                  onClick={onRequestClose}
                  isDisabled={isLoading}
                >
                  {t({ id: "common.cancel", defaultMessage: "Cancel" })}
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={handleCreateQuestionKnowledge}
                isDisabled={
                  !question.trim() || !answer.trim() || isLoading || isDisabled
                }
              >
                {t({
                  id: "cx-ai-agents-page.save-q&a",
                  defaultMessage: "Save Q&A",
                })}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
